/**
 * Put a broadcast's copies into the sender's own chats.
 *
 * Every copy is a message from me in my DM with that recipient, so it belongs
 * in that 1:1 thread and that chat belongs in my list — as on WhatsApp. For a
 * recipient I already chat with, that happens by itself: the DM is in my list,
 * its channel is joined, and the copy's `message:new` lands like any other.
 *
 * For someone I had never chatted with, nothing reached me at all. The server
 * creates the DM during the fan-out and announces it with `conversation:added`
 * — to the recipient only; the activity nudge that carries the message skips
 * the sender too; and I have no channel on a conversation I have never seen.
 * So the chat was missing from my list, with my message in it, until a reload
 * re-read the list.
 *
 * The send's own response names every copy, so the sender's client already
 * knows everything it needs. This fetches each DM it does not have and appends
 * the copy, in that order, so the new row arrives with its preview and time.
 * The append is safe for a copy that already arrived live: the store dedupes
 * by message id.
 */

import type { BroadcastCopy } from '@/sdk/instavcMessenger'
import type { Conversation as WireConversation, Message as WireMessage, MessageType } from './messenger.wire'

/** Detail fetches in flight at once: a first send to a long list stays polite. */
const FETCH_CONCURRENCY = 4

export interface SurfaceDeps {
  hasConversation: (id: string) => boolean
  getConversation: (id: string) => Promise<{ conversation: WireConversation }>
  upsertConversation: (conversation: WireConversation) => void
  appendMessage: (message: WireMessage) => void
}

/** What the sender sent — the same for every copy. */
export interface SentBroadcast {
  senderId: string
  body: string | null
  type: MessageType
  /** Fallback timestamp for a server that does not report each copy's own. */
  sentAt: string
}

export function copyToWireMessage(copy: BroadcastCopy, sent: SentBroadcast): WireMessage {
  return {
    id: copy.messageId,
    conversationId: copy.conversationId,
    senderId: sent.senderId,
    type: sent.type,
    body: sent.body,
    replyTo: null,
    callPayload: null,
    editedAt: null,
    deletedAt: null,
    createdAt: copy.createdAt ?? sent.sentAt,
    reactions: [],
    attachments: [],
  }
}

export async function surfaceBroadcastCopies(
  copies: readonly BroadcastCopy[],
  sent: SentBroadcast,
  deps: SurfaceDeps,
): Promise<void> {
  // Known chats first and at once: their rows exist, so the append alone
  // updates the thread and the preview.
  const missing: BroadcastCopy[] = []
  for (const copy of copies) {
    if (deps.hasConversation(copy.conversationId)) deps.appendMessage(copyToWireMessage(copy, sent))
    else missing.push(copy)
  }

  let next = 0
  const worker = async (): Promise<void> => {
    while (next < missing.length) {
      const copy = missing[next++]
      try {
        const { conversation } = await deps.getConversation(copy.conversationId)
        deps.upsertConversation(conversation)
      } catch {
        // The thread still gets the message below; the row turns up on the
        // next list read, as it did before this existed.
      }
      deps.appendMessage(copyToWireMessage(copy, sent))
    }
  }
  await Promise.all(Array.from({ length: Math.min(FETCH_CONCURRENCY, missing.length) }, worker))
}
